import { Provider } from 'react-redux';

import store from '../store.js';
import WebSocketProvider from '../transport/WebSocketProvider.jsx';
import {
  AuthenticationProvider,
  useAuth,
} from '../auth/AuthenticationContext.jsx';
import { JsonApiProvider } from '../api/JsonApiProvider.jsx';
import { DiagnosticsJsonApiProvider } from '../api/DiagnosticsJsonApiProvider.jsx';
import { SessionProvider } from '../session/SessionProvider.jsx';
import StoreBootstrap from '../bootstrap/StoreBootstrap.jsx';
import WsResync from '../bootstrap/WsResync.jsx';
import { FlagProvider } from '../flags/FlagContext.jsx';

// Web provider subset. The redux store (full darwin_core reducer map + a running
// rootSaga) is live, the JSON:API REST clients build inside it, authentication
// wraps those, and the WebSocket transport composes inside that. This mirrors the
// mobile nesting order (store -> jsonApi -> auth -> ... -> websocket), minus the
// BLE / geolocation / registration / native-overlay providers, which are out of
// scope for the web client.

// The auth seam (WP-0.4): the socket now opens only when the user is really
// signed in. WebSocketProvider stays prop-driven (authenticated/loaded) so it
// remains testable in isolation; this bridge feeds it the live auth state.
function AuthenticatedWebSocket({ children }) {
  const { authenticated, loaded } = useAuth();
  return (
    <WebSocketProvider authenticated={authenticated} loaded={loaded}>
      {children}
    </WebSocketProvider>
  );
}

export default function AppProviders({ children }) {
  return (
    <Provider store={store}>
      <JsonApiProvider>
        <DiagnosticsJsonApiProvider>
          <AuthenticationProvider>
            {/* Probes the signed-in user's account (read-only) and exposes the
                session flags the SessionGate reads. Sits inside auth + JSON:API
                (both of which it needs) and OUTSIDE the socket, so session state
                is an ancestor of `children` — the dashboard, mounted as
                `children` inside the socket, therefore sees BOTH session and
                WebSocket contexts. */}
            <SessionProvider>
              {/* Seeds the read model on sign-in and resets it on sign-out. Sits
                  inside the store, JSON:API and auth providers so it can read all
                  three; renders nothing. */}
              <StoreBootstrap />
              {/* The user's development flags (pilot / debug): eligibility from
                  the auth context above, the switched-on set from localStorage.
                  The app mounts its FlagProvider at the same depth (inside auth
                  and session). */}
              <FlagProvider>
                <AuthenticatedWebSocket>
                  {/* Re-seeds the read model (REST homes fetch) on WS reconnect,
                      so state changed during a disconnect — desiredState
                      included — heals. Mounted inside the socket so it can read
                      its status. */}
                  <WsResync />
                  {children}
                </AuthenticatedWebSocket>
              </FlagProvider>
            </SessionProvider>
          </AuthenticationProvider>
        </DiagnosticsJsonApiProvider>
      </JsonApiProvider>
    </Provider>
  );
}
